// Shared pieces of the stay-search UI, used by the homepage hero search and by
// the results-page search bar so both open the same panels with the same
// behaviour. On phones a panel takes over the screen; from md up it is an
// anchored dropdown positioned by the caller.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SearchIcon, PinIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "./icons.jsx";
import { iso, parseISO, fmtLong } from "../lib/dates.js";

// How many months the phone calendar scrolls through. Desktop keeps the
// two-month view with arrows; phones scroll a continuous list instead, which
// is how every travel app does it and removes a whole tap per month.
export const MOBILE_MONTHS = 14;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return isMobile;
}

// Phones only: while a panel owns the screen, the page behind it must not
// scroll — otherwise flicking the month list drags the page with it.
export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [active]);
}

export function monthList(today, count = MOBILE_MONTHS) {
  return Array.from({ length: count }, (_, i) => new Date(today.getFullYear(), today.getMonth() + i, 1));
}

// Only as many week rows as the month actually needs. A fixed six rows left a
// blank row under most months, which is invisible in a side-by-side desktop
// grid but opens a dead gap between months in the phone's scrolling list.
export function monthDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Math.ceil((first.getDay() + daysInMonth) / 7) * 7;
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: cells }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

export function CalendarMonth({ month, today, start, end, phase, hover, onHover, onPick }) {
  return (
    <div className="w-full">
      <h4 className="text-center font-semibold text-base md:text-sm text-ink mb-2">{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h4>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {monthDays(month).map(day => {
          const val = iso(day);
          const outside = day.getMonth() !== month.getMonth();
          const past = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const previewEnd = !end && phase === "end" && hover > start ? hover : "";
          const rangeEnd = end || previewEnd;
          const inRange = !!start && !!rangeEnd && val > start && val < rangeEnd;
          const isPreview = val === previewEnd;
          const isStart = val === start;
          const isEnd = val === end || isPreview;
          const disabled = past || outside;
          let classes = "relative aspect-square flex items-center justify-center text-sm rounded-full font-medium select-none";
          if (disabled) classes += " text-ink-muted/40 cursor-not-allowed";
          else classes += " cursor-pointer hover:bg-ocean-50 text-ink";
          if (outside) classes += " invisible";
          if (inRange) classes += " !rounded-none bg-ocean-100/70 text-ocean-800";
          if (isStart || isEnd) classes += " bg-ocean-600 text-white hover:bg-ocean-700 z-10";
          if (isStart && rangeEnd) classes += " !rounded-l-full !rounded-r-none";
          if (isEnd && start && val !== start) classes += " !rounded-r-full !rounded-l-none";
          if (isStart && isEnd) classes += " !rounded-full";
          return (
            <button type="button" key={val} disabled={disabled} className={classes} onClick={() => onPick(val)} onMouseEnter={() => onHover(val)} onMouseLeave={() => onHover("")}>{day.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

// A search step. On phones it takes over the screen — header with a close
// button, scrolling body, action pinned to the bottom — because a dropdown
// left the list of islands half off-screen and the calendar unusable. From md
// up it is an anchored dropdown, positioned by the caller's `desktop` classes.
export function Panel({ title, onClose, children, footer, isMobile, desktop = "", bodyClass = "", footerClass = "" }) {
  const panel = (
    <div data-search-panel className={`fixed inset-0 z-[80] flex flex-col bg-white md:absolute md:inset-auto md:z-40 md:top-[calc(100%+8px)] md:block md:rounded-3xl md:shadow-cardHover md:ring-1 md:ring-ink/5 ${desktop}`}>
      <div className="flex shrink-0 items-center gap-3 border-b border-ink/10 px-4 py-3 md:hidden">
        <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink">
          <CloseIcon size={18}/>
        </button>
        <div className="flex-1 text-center font-display text-lg text-ink">{title}</div>
        <span className="h-10 w-10 shrink-0" aria-hidden/>
      </div>
      <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain md:flex-none md:overflow-visible ${bodyClass}`}>
        {children}
      </div>
      {footer && (
        <div className={`shrink-0 border-t border-ink/10 bg-white px-4 py-3 md:border-ink/5 ${footerClass}`}>
          {footer}
        </div>
      )}
    </div>
  );
  // A search bar can sit inside a positioned, z-indexed wrapper, which traps a
  // sheet below the sticky site header no matter how high its own z-index
  // goes. On phones it goes to <body> to escape that stacking context.
  return isMobile ? createPortal(panel, document.body) : panel;
}

export function GuestRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-base md:text-sm font-semibold text-ink">{label}</div>
        <div className="text-xs text-ink-muted">{hint}</div>
      </div>
      <div className="flex items-center gap-3 md:gap-2">
        <button type="button" onClick={() => onChange(Math.max(min, value-1))} disabled={value <= min} className="h-11 w-11 md:h-9 md:w-9 rounded-full border border-ink/15 flex items-center justify-center text-lg md:text-base text-ink hover:border-ocean-400 disabled:opacity-30">−</button>
        <span className="w-8 text-center text-base md:text-sm font-medium text-ink">{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value+1))} disabled={value >= max} className="h-11 w-11 md:h-9 md:w-9 rounded-full border border-ink/15 flex items-center justify-center text-lg md:text-base text-ink hover:border-ocean-400 disabled:opacity-30">+</button>
      </div>
    </div>
  );
}

// Adults plus one age selector per child. Children are carried as an array of
// ages everywhere, because that is what the rates API wants — a bare count
// would have to invent ages, and inventing them changes the price.
export function GuestsPanelContent({ adults, setAdults, childAges, setChildAges, maxAdults = 12, maxChildren = 8 }) {
  const setCount = n => {
    if (n < childAges.length) setChildAges(childAges.slice(0, n));
    else setChildAges([...childAges, ...Array.from({ length: n - childAges.length }, () => 8)]);
  };
  return (
    <>
      <GuestRow label="Adults" hint="Ages 13+" value={adults} min={1} max={maxAdults} onChange={setAdults}/>
      <div className="h-px bg-ink/5 my-4 md:my-3"/>
      <GuestRow label="Children" hint="Ages 0–17" value={childAges.length} min={0} max={maxChildren} onChange={setCount}/>
      {childAges.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-ink/5 pt-4">
          {childAges.map((age, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <label className="text-sm text-ink-soft">Child {i + 1} age</label>
              <select
                value={age}
                onChange={e => setChildAges(childAges.map((a, idx) => idx === i ? Number(e.target.value) : a))}
                className="pill-input py-2 px-3 text-sm w-28"
              >
                {Array.from({ length: 18 }).map((_, n) => <option key={n} value={n}>{n < 1 ? "<1" : n}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function SuggestionList({ suggestions, onIsland, onPlace, onProperty, emptyText = "No matches — try a nearby town." }) {
  const hasAny = suggestions.islands.length + suggestions.places.length + suggestions.properties.length > 0;
  return (
    <>
      {!hasAny && <div className="px-4 py-8 text-center text-sm text-ink-muted">{emptyText}</div>}

      {suggestions.islands.length > 0 && (
        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Islands</div>
      )}
      {suggestions.islands.map(i => (
        <button key={i.value} type="button" onClick={() => onIsland(i)} className="w-full flex items-start gap-3 rounded-2xl px-3 py-3 md:py-2.5 hover:bg-sand-50 text-left">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-50 text-ocean-600 mt-0.5"><PinIcon size={16}/></span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">{i.display}</div>
            <div className="text-xs text-ink-muted mt-0.5">{i.blurb}</div>
          </div>
        </button>
      ))}

      {suggestions.places.length > 0 && (
        <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Towns &amp; areas</div>
      )}
      {suggestions.places.map(place => (
        <button key={`${place.area}|${place.island}`} type="button" onClick={() => onPlace(place)} className="w-full flex items-start gap-3 rounded-2xl px-3 py-3 md:py-2.5 hover:bg-sand-50 text-left">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-50 text-ocean-600 mt-0.5"><PinIcon size={16}/></span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">{place.area}</div>
            <div className="text-xs text-ink-muted mt-0.5">{place.island}{place.count ? ` · ${place.count.toLocaleString()} properties` : ""}</div>
          </div>
        </button>
      ))}

      {suggestions.properties.length > 0 && (
        <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Properties</div>
      )}
      {suggestions.properties.map(p => (
        <button key={p.id} type="button" onClick={() => onProperty(p)} className="w-full flex items-start gap-3 rounded-2xl px-3 py-3 md:py-2.5 hover:bg-sand-50 text-left">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sunset-50 text-base leading-none mt-0.5" aria-hidden>🏠</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink line-clamp-2 leading-snug">{p.name}</div>
            <div className="text-xs text-ink-muted mt-0.5">Property · {[p.area || p.city, p.island].filter(Boolean).join(" · ")}</div>
          </div>
        </button>
      ))}
    </>
  );
}

export function WherePanelContent({ value, onChange, onClear, suggestions, onIsland, onPlace, onProperty }) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-3.5 md:py-3 rounded-2xl bg-sand-50 border border-ink/5">
        <SearchIcon size={16} className="text-ink-muted shrink-0"/>
        <input
          autoFocus
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Search islands, towns, or properties"
          className="w-full bg-transparent text-base md:text-sm text-ink placeholder:text-ink-muted focus:outline-none"
        />
        {value && <button type="button" onClick={onClear} className="text-ink-muted hover:text-ink shrink-0"><CloseIcon size={16}/></button>}
      </div>
      <div className="mt-2 md:max-h-[28rem] md:overflow-auto">
        <SuggestionList suggestions={suggestions} onIsland={onIsland} onPlace={onPlace} onProperty={onProperty}/>
      </div>
    </>
  );
}

export function DatesPanelContent({ today, checkin, checkout, phase, hover, setHover, onPick, calMonth, setCalMonth }) {
  const months = monthList(today);
  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-4 md:gap-6 text-sm">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-in</div>
            <div className="font-semibold text-ink">{checkin ? fmtLong(checkin) : "Select date"}</div>
          </div>
          <div className="text-ink-muted">→</div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-out</div>
            <div className="font-semibold text-ink">{checkout ? fmtLong(checkout) : "Select date"}</div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-1">
          <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()-1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Previous month"><ChevronLeftIcon size={16}/></button>
          <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Next month"><ChevronRightIcon size={16}/></button>
        </div>
      </div>

      {/* Phones scroll a continuous run of months. */}
      <div className="space-y-7 md:hidden">
        {months.map(m => (
          <CalendarMonth key={iso(m)} month={m} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={onPick}/>
        ))}
      </div>

      <div className="hidden md:grid md:grid-cols-2 gap-6">
        <CalendarMonth month={calMonth} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={onPick}/>
        <CalendarMonth month={new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1)} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={onPick}/>
      </div>
    </>
  );
}

// Range picking shared by both search bars: first tap sets check-in and clears
// check-out, second tap closes the range unless it lands on or before check-in.
export function makeDatePicker({ checkin, phase, setPhase, setHover, onChange }) {
  return v => {
    if (phase === "start" || !checkin || v <= checkin) {
      onChange(v, "");
      setPhase("end");
    } else {
      onChange(checkin, v);
      setPhase("start");
    }
    setHover("");
  };
}

export { parseISO };
